import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Player } from "../../api/Player.types";
import { createPlayer, getPlayerById, updatePlayer } from "../../services/PlayerService";

type AddPlayerProps = {
    player: Player | null
    mode: 'add' | 'edit'
    cancelButtonHandler: (e: any) => void
    onSaved?: (player: Player) => void
    title?: string
}

export const AddPlayer = ({ cancelButtonHandler, onSaved, player, mode, title }: AddPlayerProps) => {
    const [id, setId] = useState<string>(`${player?.id}`);
    const [firstName, setFirstName] = useState(`${player?.firstName}`);
    const [lastName, setLastName] = useState(`${player?.lastName}`);
    const [score, setScore] = useState(`${player?.score}`);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onSubmitButtonHandler = async (e: any) => {
        e.preventDefault();
        const playerId = Number(id.trim());
        if (mode === 'add' && (!/^-?\d+$/.test(id.trim()) || !Number.isSafeInteger(playerId) || playerId === 0)) {
            setError('ID must be a whole number other than 0');
            return;
        }
        const data: Player = {
            id: playerId,
            firstName: firstName,
            lastName: lastName,
            score: Number(score)
        }
        setSaving(true);
        setError(null);
        try {
            if (mode === 'add') {
                // saving over an existing id would silently replace that player
                const existing = await getPlayerById(playerId);
                if (existing?.id !== undefined) {
                    setError(`ID ${playerId} already belongs to ${`${existing.firstName || ''} ${existing.lastName || ''}`.trim() || 'another player'}`);
                    setSaving(false);
                    return;
                }
                await createPlayer(data);
            } else {
                await updatePlayer(id, data);
            }
        } catch (err: any) {
            setError(err?.message || 'Could not save the player');
            setSaving(false);
            return;
        }

        onSaved?.(data);
        cancelButtonHandler(e);
    }

    return (
        <Box component="form" onSubmit={onSubmitButtonHandler} sx={{ p: 3, width: 360, maxWidth: "100%" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {title ?? (mode === 'add' ? 'Add Player' : 'Edit Player')}
            </Typography>
            <Stack spacing={2}>
                <TextField
                    label="ID"
                    value={id}
                    onChange={(e) => setId(e.target.value)}
                    disabled={mode !== 'add'}
                    helperText={mode === 'add' ? "Telegram user id, or keep the generated one" : undefined}
                    fullWidth
                />
                <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    autoFocus
                />
                <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Score"
                    type="number"
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    fullWidth
                />
            </Stack>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
                <Button onClick={cancelButtonHandler} color="inherit">Cancel</Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {saving ? 'Saving…' : mode === 'add' ? 'Add' : 'Update'}
                </Button>
            </Stack>
        </Box>
    )
}

export default AddPlayer;
